import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useStickers } from '../hooks/useStickers';
import { useFriendRequests } from '../hooks/useFriendRequests';
import { PageShell } from '../components/layout/PageShell';
import type { TabKey } from '../components/layout/Tabs';
import { AlbumTab } from '../tabs/AlbumTab';
import { TrocasTab } from '../tabs/TrocasTab';
import { AmigosTab } from '../tabs/AmigosTab';
import ParceirosPage from './ParceirosPage';
import PerfilPage from './PerfilPage';
import { useTutorial } from '../components/tutorial/TutorialProvider';
import { TUTORIAL_FORCE_KEY, TUTORIAL_STORAGE_KEY } from '../components/tutorial/steps';

interface Props {
  user: User;
}

const VALID_TABS: TabKey[] = ['album', 'trocas', 'amigos', 'parceiros', 'perfil'];

export default function AlbumPage({ user }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const active: TabKey = (VALID_TABS.includes(tabParam as TabKey) ? tabParam : 'album') as TabKey;

  const { counts, loading, handleClick, handleDecrement, bulkUpdate, resetDuplicates, restoreFromSnapshot, stats, tradeableCodes } = useStickers(user.id);
  const { incomingCount } = useFriendRequests(user.id);
  const { start: startTutorial } = useTutorial();
  const autoStartedRef = useRef(false);

  useEffect(() => {
    if (autoStartedRef.current || loading) return;
    let seen: string | null = null;
    let forced: string | null = null;
    try {
      seen = localStorage.getItem(TUTORIAL_STORAGE_KEY);
      forced = localStorage.getItem(TUTORIAL_FORCE_KEY);
    } catch {
      seen = '1';
    }
    if (forced) {
      try {
        localStorage.removeItem(TUTORIAL_FORCE_KEY);
        localStorage.removeItem(TUTORIAL_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      autoStartedRef.current = true;
      startTutorial();
      return;
    }
    if (!seen) {
      autoStartedRef.current = true;
      startTutorial();
    }
  }, [loading, startTutorial]);

  const handleTabChange = (tab: TabKey) => {
    if (tab === 'album') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <PageShell
      user={user}
      active={active}
      onTabChange={handleTabChange}
      onSignOut={handleSignOut}
      badge={{ amigos: incomingCount }}
    >
      {active === 'album' && (
        <AlbumTab
          user={user}
          counts={counts}
          loading={loading}
          stats={stats}
          onStickerClick={handleClick}
          onStickerReset={handleDecrement}
          onBulkImport={bulkUpdate}
          onResetDuplicates={resetDuplicates}
          onRestoreFromSnapshot={restoreFromSnapshot}
        />
      )}
      {active === 'trocas' && (
        <TrocasTab
          user={user}
          counts={counts}
          allCodes={tradeableCodes}
          onChangeTab={handleTabChange}
        />
      )}
      {active === 'amigos' && (
        <AmigosTab user={user} myCounts={counts} allCodes={tradeableCodes} />
      )}
      {active === 'parceiros' && (
        <ParceirosPage embedded user={user} />
      )}
      {active === 'perfil' && (
        <PerfilPage user={user} embedded onChangeTab={handleTabChange} />
      )}
    </PageShell>
  );
}
